import { ReactNode, useEffect, useRef } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { ThemeMode, useLightMode } from '@/hooks/useLightMode'
import { useTranslation } from 'react-i18next'
import { BiDoorOpen, BiVideo } from 'react-icons/bi'
import { FiUser } from 'react-icons/fi'
import { HiOutlineInformationCircle } from 'react-icons/hi'
import { MdOutlineBadge } from 'react-icons/md'

import { Alert, BoxProps, Flex, Icon, Text } from '@chakra-ui/react'

import {
  AlarmIcon as DarkAlarmIcon,
  Alert as DarkAlert,
  CameraIcon as DarkCameraIcon,
  DoorIcon as DarkDoorIcon,
  TraceLogEvent,
  tokenPrefix,
  tokenVar,
} from '@hivewatch/dark'

import { Alarm } from '@/components/icons'
import { DeviceType, EventType } from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'

import { EventsT } from '../../shared/types/traceLogTypes'
import {
  getDisplayBadgeNumber,
  getEventName,
} from '../../shared/utils/traceLogUtils'

interface EventItemIProps {
  event: EventsT
  isCauseOfIncident?: boolean
  isHighlightedFromResolution?: boolean
  isFromResolvedAction?: boolean
  shouldShowDeviceInfo?: boolean
}

interface EventDetailItemIProps extends BoxProps {
  label: string
  icon: ReactNode
}

export const EventItem = ({
  event,
  isCauseOfIncident,
  isHighlightedFromResolution,
  isFromResolvedAction,
  shouldShowDeviceInfo,
}: EventItemIProps) => {
  const {
    detectedAt,
    type,
    cardHolderName,
    badgeNumber,
    deviceName,
    deviceType,
  } = event

  const { t } = useTranslation()
  const isTraceLogsPiiEnabled = useFeatureFlag(FeatureFlag.traceLogsPii)
  const isRebrand = useFeatureFlag(FeatureFlag.incidentDrawerRebrand)
  const mode = useLightMode()
  const isHighlightedFromIncident =
    isCauseOfIncident && type !== EventType.DoorUnlocked
  const isHighlighted = isHighlightedFromResolution || isHighlightedFromIncident
  const ref = useRef<HTMLDivElement>(null)
  const hasDeviceInfo = shouldShowDeviceInfo && deviceName
  const displayBadgeNumber = getDisplayBadgeNumber(badgeNumber)
  const shouldShowTailgateAlert =
    isCauseOfIncident && type === EventType.DoorUnlocked

  const getHighlightStyles = () => {
    if (isHighlightedFromIncident) {
      return { bg: 'bg.400', borderColor: 'border.400' }
    }
    return { bg: 'white', borderColor: 'border.200' }
  }

  const { bg, borderColor } = getHighlightStyles()

  useEffect(() => {
    if (isHighlighted && ref?.current) {
      ref.current?.scrollIntoView({ behavior: 'auto', block: 'center' })
    }
  }, [isHighlighted])

  if (isRebrand) {
    const hasCallout = isFromResolvedAction || shouldShowTailgateAlert
    const callout = hasCallout ? (
      <>
        {isFromResolvedAction && (
          <DarkAlert
            colorScheme={mode}
            description={t('incidents:drawer_trace_log_resolution_alert')}
            size='compact'
            variant='success'
          />
        )}
        {shouldShowTailgateAlert && (
          <DarkAlert
            colorScheme={mode}
            description={t('incidents:drawer_trace_log_tailgate_alert')}
            size='compact'
            variant='info'
          />
        )}
      </>
    ) : undefined

    return (
      <TraceLogEvent
        // Events without a badge omit the credential row entirely (ENG-32092,
        // superseding the ENG-31461 '0' placeholder). The PII flag still
        // hides it regardless.
        badgeNumber={isTraceLogsPiiEnabled ? displayBadgeNumber : undefined}
        callout={callout}
        cardHolderName={
          isTraceLogsPiiEnabled && cardHolderName ? cardHolderName : undefined
        }
        colorScheme={mode}
        data-testid='traceLog_eventItem'
        deviceIcon={
          hasDeviceInfo
            ? getDarkEventDeviceIcon(
                deviceType,
                mode,
                isHighlightedFromIncident
              )
            : undefined
        }
        deviceName={hasDeviceInfo ? deviceName : undefined}
        ref={ref}
        timestamp={formatDateTime(detectedAt)}
        title={getEventName(event)}
        variant={isHighlightedFromIncident ? 'alarm' : 'default'}
      />
    )
  }

  return (
    <Flex
      bg={bg}
      borderColor={borderColor}
      borderRadius='10px'
      borderWidth='1px'
      color='black'
      data-testId='traceLog_eventItem'
      direction='column'
      px='16px'
      py='8px'
      ref={ref}
    >
      {isFromResolvedAction && <EventItemAlert variant='resolution' />}
      {shouldShowTailgateAlert && <EventItemAlert variant='tailgate' />}
      <Flex alignItems='center' direction='row' justifyContent='space-between'>
        <Text fontSize='14px' fontWeight='500' maxW='65%' noOfLines={2}>
          {getEventName(event)}
        </Text>
        <Text fontSize='12px' fontWeight='400'>
          {formatDateTime(detectedAt)}
        </Text>
      </Flex>

      <Flex direction='column' fontSize='12px' fontWeight='400' noOfLines={2}>
        {isTraceLogsPiiEnabled && cardHolderName && (
          <EventDetailItem
            icon={<FiUser size='12px' />}
            label={cardHolderName}
            mt='5px'
          />
        )}

        {isTraceLogsPiiEnabled && displayBadgeNumber && (
          <EventDetailItem
            icon={<MdOutlineBadge size='12px' />}
            label={displayBadgeNumber}
          />
        )}
        {hasDeviceInfo && (
          <EventDetailItem
            icon={getEventDeviceIcon(deviceType)}
            label={deviceName}
          />
        )}
      </Flex>
    </Flex>
  )
}

export const EventDetailItem = ({
  label,
  icon,
  ...rest
}: EventDetailItemIProps) => (
  <Flex alignItems='center' direction='row' gap='10px' {...rest}>
    {icon}
    <Text noOfLines={2} width='95%'>
      {label}
    </Text>
  </Flex>
)

export const getEventDeviceIcon = (type: DeviceType) => {
  switch (type) {
    case DeviceType.Alarm:
      return <Icon as={Alarm} boxSize='12px' strokeColor='black' />
    case DeviceType.Camera:
      return <BiVideo color='black' size='12px' />

    default:
      return <BiDoorOpen color='black' size='12px' />
  }
}

export const getDarkEventDeviceIcon = (
  type: DeviceType,
  mode: ThemeMode,
  isAlarm = false
) => {
  // Match the detail-row text color the TraceLogEvent rows use — including
  // the error tint on alarm rows, so the device icon doesn't stay gray while
  // the rest of the row turns red.
  const prefix = tokenPrefix(mode)
  const color = tokenVar(
    `colors.${prefix}${isAlarm ? 'status.error-text' : 'text.secondary'}`
  )
  switch (type) {
    case DeviceType.Alarm:
      return <DarkAlarmIcon color={color} size={16} />
    case DeviceType.Camera:
      return <DarkCameraIcon color={color} size={16} />
    default:
      return <DarkDoorIcon color={color} size={16} />
  }
}

interface EventItemAlertIProps {
  variant: 'tailgate' | 'resolution'
}

export const EventItemAlert = ({ variant }: EventItemAlertIProps) => {
  const { t } = useTranslation()
  const isResolution = variant === 'resolution'
  const message = isResolution
    ? t('incidents:drawer_trace_log_resolution_alert')
    : t('incidents:drawer_trace_log_tailgate_alert')
  const bgColor = isResolution ? '#e8fae9' : '#DBE9FE'
  const textColor = isResolution ? 'green.600' : '#1D40B0'

  return (
    <Alert bg={bgColor} borderRadius='10px' mb='10px' p='8px' status='info'>
      <Icon
        as={HiOutlineInformationCircle}
        boxSize='20px'
        color={textColor}
        mr='10px'
      />
      <Text color={textColor} fontSize='12px' fontWeight='300'>
        {message}
      </Text>
    </Alert>
  )
}
